import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, ListSubheader, Menu, MenuItem, Snackbar } from '@mui/material';
import { collection, doc, getDoc, getDocFromServer, getDocs, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useTemplateStatuses } from './hubTemplates';
import { TEMPLATE_LANG_CODES } from '../../utils/whatsappTemplateDrafts';
import type { AutoReplyLang } from '../../utils/whatsappAutoReplyFlows';

interface Props {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  leadId: string;
  leadName: string;
  phone: string;
  projectId?: string | null;
  projectName?: string | null;
  lastInboundAt?: Date | null;
}

const LANGUAGES: Array<[AutoReplyLang, string]> = [
  ['en', 'English'], ['gu', 'ગુજરાતી'], ['hi', 'हिंदी'],
];
const CHOOSE_LABEL: Record<AutoReplyLang, string> = {
  en: 'Choose project', gu: 'પ્રોજેક્ટ પસંદ કરો', hi: 'प्रोजेक्ट चुनें',
};
const DAY_MS = 24 * 60 * 60 * 1000;
const SEND_MARGIN_MS = 30 * 1000;
const asMillis = (value: unknown): number => {
  if (value && typeof (value as { toMillis?: () => number }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }
  return 0;
};

export default function LeadBrochureMenu({ anchorEl, onClose, leadId, leadName, phone, projectId, projectName, lastInboundAt }: Props) {
  const open = Boolean(anchorEl);
  const statuses = useTemplateStatuses();
  const [project, setProject] = useState<{ id: string; name: string; brochureUrl: string } | null>(null);
  const [projectResolved, setProjectResolved] = useState(false);
  const [projectError, setProjectError] = useState('');
  const [lastInboundMs, setLastInboundMs] = useState(0);
  const [windowChecked, setWindowChecked] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const [sendingLanguage, setSendingLanguage] = useState<AutoReplyLang | null>(null);
  const [notice, setNotice] = useState<{ severity: 'error' | 'success'; text: string } | null>(null);

  const phoneDigits = String(phone || '').replace(/\D/g, '');
  const phone10 = phoneDigits.length === 12 && phoneDigits.startsWith('91') ? phoneDigits.slice(2) : phoneDigits;
  const chatInboundMs = lastInboundAt?.getTime() || 0;
  const regularMessage = windowChecked && lastInboundMs > 0 && nowMs - lastInboundMs < DAY_MS - SEND_MARGIN_MS;
  const hasProject = Boolean(project);
  const templateBase = hasProject ? 'swarna_manual_brochure' : 'swarna_manual_choose_project';
  const brochureReady = !hasProject || Boolean(project?.brochureUrl && /^https:\/\//i.test(project.brochureUrl));
  const firstName = String(leadName || '').trim().split(/\s+/)[0] || 'Customer';

  useEffect(() => {
    if (!open) return;
    setProjectResolved(false);
    setProjectError('');
    if (!projectId && !String(projectName || '').trim()) {
      setProject(null);
      setProjectResolved(true);
      return;
    }
    let cancelled = false;
    const loadProject = async (): Promise<{ id: string; name: string; brochureUrl: string } | null> => {
      if (projectId) {
        const snap = await getDoc(doc(db, 'projects', projectId));
        if (!snap.exists()) return null;
        const data = snap.data();
        return { id: snap.id, name: String(data.name || '').trim(), brochureUrl: String(data.brochureUrl || '').trim() };
      }
      const matches = (await getDocs(collection(db, 'projects'))).docs.filter((item) =>
        String(item.data().name || '').trim().toLowerCase() === String(projectName || '').trim().toLowerCase());
      if (matches.length !== 1) return null;
      const data = matches[0].data();
      return { id: matches[0].id, name: String(data.name || '').trim(), brochureUrl: String(data.brochureUrl || '').trim() };
    };
    loadProject().then((value) => {
      if (cancelled) return;
      setProject(value);
      if (!value) setProjectError('Saved project not found. Update the lead’s project first.');
    }).catch((cause) => {
      if (!cancelled) setProjectError(String(cause?.message || cause));
    }).finally(() => {
      if (!cancelled) setProjectResolved(true);
    });
    return () => { cancelled = true; };
  }, [open, projectId, projectName]);

  useEffect(() => {
    if (!open || !/^\d{10}$/.test(phone10)) return;
    setWindowChecked(false);
    setNowMs(Date.now());
    const unsubscribe = onSnapshot(doc(db, 'whatsappContactState', phone10),
      (snap) => {
        setLastInboundMs(Math.max(asMillis(snap.data()?.lastInboundAt), chatInboundMs));
        setWindowChecked(true);
        setNowMs(Date.now());
      },
      () => {
        setLastInboundMs(chatInboundMs);
        setWindowChecked(chatInboundMs > 0);
        if (!chatInboundMs) setNotice({ severity: 'error', text: 'Could not check the WhatsApp reply window.' });
      });
    const timer = window.setInterval(() => setNowMs(Date.now()), 15_000);
    return () => { unsubscribe(); window.clearInterval(timer); };
  }, [open, phone10, chatInboundMs]);

  const messageText = (language: AutoReplyLang) => String(statuses.effective(templateBase)?.body[language] || '')
    .replaceAll('{{1}}', firstName)
    .replaceAll('{{2}}', project?.name || 'Project');
  const canSend = (language: AutoReplyLang) => !sendingLanguage && projectResolved && !projectError &&
    windowChecked && /^\d{10}$/.test(phone10) && brochureReady &&
    (regularMessage || (!statuses.loading && Boolean(statuses.versionsFor(templateBase, language).sending)));

  const send = async (language: AutoReplyLang) => {
    if (!canSend(language)) return;
    setSendingLanguage(language);
    setNotice(null);
    try {
      // A menu can stay open across the 24-hour boundary, so choose the route
      // from a fresh server read immediately before sending.
      let inboundMs = chatInboundMs;
      try {
        const contact = await getDocFromServer(doc(db, 'whatsappContactState', phone10));
        inboundMs = Math.max(inboundMs, asMillis(contact.data()?.lastInboundAt));
      } catch (readError) {
        if (!inboundMs) throw readError;
      }
      setLastInboundMs(inboundMs);
      setNowMs(Date.now());
      const useRegularMessage = inboundMs > 0 && Date.now() - inboundMs < DAY_MS - SEND_MARGIN_MS;
      const text = messageText(language);
      const common = { to: `91${phone10}`, leadId, projectId: project?.id || undefined, source: 'lead_manual_brochure' };
      if (useRegularMessage) {
        if (hasProject) {
          await httpsCallable(functions, 'whatsappSendMessage')({
            ...common, mediaType: 'document', mediaUrl: project!.brochureUrl,
            mediaFilename: `${project!.name || 'Project'} Brochure.pdf`,
            mediaMimeType: 'application/pdf', text,
          });
        } else {
          await httpsCallable(functions, 'whatsappSendMessage')({
            ...common, text, rawMessageType: 'interactive',
            rawMessagePayload: { type: 'interactive', interactive: {
              type: 'button', body: { text }, action: { buttons: [
                { type: 'reply', reply: { id: 'ar:brochurepick:-', title: CHOOSE_LABEL[language] } },
              ] },
            } },
          });
        }
      } else {
        const templateName = statuses.versionsFor(templateBase, language).sending;
        if (!templateName) throw new Error('The reply window closed. This language’s Meta template is still awaiting approval.');
        const bodyValues = hasProject ? [firstName, project!.name] : [firstName];
        const templateComponents: unknown[] = [
          { type: 'body', parameters: bodyValues.map((value) => ({ type: 'text', text: value })) },
        ];
        if (hasProject) {
          templateComponents.unshift({ type: 'header', parameters: [{ type: 'document', document: {
            link: project!.brochureUrl, filename: `${project!.name || 'Project'} Brochure.pdf`,
          } }] });
        } else {
          templateComponents.push({ type: 'button', sub_type: 'quick_reply', index: '0',
            parameters: [{ type: 'payload', payload: 'ar:brochurepick:-' }] });
        }
        await httpsCallable(functions, 'whatsappSendMessage')({
          ...common, templateName, templateLanguage: TEMPLATE_LANG_CODES[language],
          templateComponents, resolvedText: text,
          metadata: { source: 'lead_manual_brochure', template: templateBase },
        });
      }
      onClose();
      setNotice({ severity: 'success', text: hasProject ? 'Brochure submitted to WhatsApp. Check delivery in chat.' : 'Project choice submitted to WhatsApp. Check delivery in chat.' });
    } catch (cause: any) {
      setNotice({ severity: 'error', text: String(cause?.message || 'Could not send the WhatsApp message.') });
    } finally {
      setSendingLanguage(null);
    }
  };

  const statusText = !/^\d{10}$/.test(phone10) ? 'Valid WhatsApp number required' :
    !projectResolved ? 'Checking brochure…' : projectError ? projectError :
    !brochureReady ? 'Project brochure PDF missing' :
    !windowChecked ? 'Checking 24-hour reply window…' :
    regularMessage ? 'Regular reply · no Meta charge' : 'Meta template · may be charged';

  return <>
    <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
      <ListSubheader sx={{ lineHeight: 1.4, py: 1, minWidth: 220 }}>
        <Box sx={{ fontWeight: 700, color: 'text.primary' }}>{hasProject ? `Send ${project?.name} brochure PDF` : 'Choose project, then send brochure'}</Box>
        <Box sx={{ fontSize: 12 }}>{statusText}</Box>
      </ListSubheader>
      {LANGUAGES.map(([language, label]) => {
        const approved = Boolean(statuses.versionsFor(templateBase, language).sending);
        return <MenuItem key={language} disabled={!canSend(language)} onClick={() => void send(language)}>
          {sendingLanguage === language && <CircularProgress size={16} sx={{ mr: 1 }} />}
          {label}{!regularMessage && !approved ? ' · Awaiting Meta approval' : ''}
        </MenuItem>;
      })}
    </Menu>
    <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert severity={notice?.severity || 'info'} onClose={() => setNotice(null)} sx={{ width: '100%' }}>
        {notice?.text || ''}
      </Alert>
    </Snackbar>
  </>;
}
